import React, { useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  SwipeableDrawer,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  VideoLibrary,
  Menu as MenuIcon,
  Logout,
  ArrowBack,
  School,
  CardMembership,
  PeopleAlt,
  VideoCall,
  MailOutline,
  Email as EmailBlastIcon,
  Hub as LinksIcon,
  EmojiEvents as EngagementIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  WhatsApp as WhatsAppIcon,
  GroupAdd as ReferralsIcon,
  LocalOffer as CouponsIcon,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import NotificationBell from "./NotificationBell";

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_MOBILE = 280;

const ADMIN_COLORS = {
  sidebar:            "#0A1628",                   // Dr.Paw navy
  sidebarHover:       "rgba(21,101,192,0.18)",     // blue tint on hover
  sidebarActive:      "rgba(21,101,192,0.28)",     // blue tint active
  sidebarBorder:      "rgba(255,255,255,0.07)",
  accent:             "#1565C0",                   // Dr.Paw blue
  accentLight:        "#42A5F5",                   // blue light
  topbar:             "#FFFFFF",
  topbarBorder:       "rgba(21,101,192,0.1)",
  bg:                 "#F0F7FF",                   // Dr.Paw page bg
  text:               "#0A1628",                   // navy
  textMuted:          "#78909C",
  textSidebar:        "rgba(255,255,255,0.55)",
  textSidebarActive:  "#FFFFFF",
  iconActive:         "#42A5F5",                   // blue light for active icons
};

interface NavItem {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  section?: boolean;
}

const navItems: NavItem[] = [
  { label: "Dashboard",       icon: <DashboardIcon />,  path: "/admin" },
  { label: "Courses",         icon: <School />,          path: "/admin/courses" },
  { label: "Subscriptions",   icon: <CardMembership />,  path: "/admin/subscriptions" },
  { label: "Users",           icon: <PeopleAlt />,       path: "/admin/users" },
  { label: "Zoom Meetings",   icon: <VideoCall />,       path: "/admin/zoom-meetings" },
  { label: "Contact",         icon: <MailOutline />,     path: "/admin/contact" },
  { label: "Email Blast",     icon: <EmailBlastIcon />,  path: "/admin/email-blast" },
  // ── Marketing section ──
  { label: "MARKETING", section: true },
  { label: "Links & Content",  icon: <LinksIcon />,      path: "/admin/marketing" },
  { label: "Engagement Tools", icon: <EngagementIcon />, path: "/admin/marketing/engagement" },
  { label: "WhatsApp Broadcast", icon: <WhatsAppIcon />, path: "/admin/marketing/whatsapp" },
  { label: "Referrals",        icon: <ReferralsIcon />, path: "/admin/marketing/referrals" },
  { label: "Coupons",          icon: <CouponsIcon />,  path: "/admin/marketing/coupons" },
  { label: "Analytics & SEO",  icon: <AnalyticsIcon />,  path: "/admin/marketing/analytics" },
  // ── Configuration ──
  { label: "CONFIGURATION", section: true },
  { label: "Settings",       icon: <SettingsIcon />,    path: "/admin/settings" },
];

const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const isActive = (path: string) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: ADMIN_COLORS.sidebar,
      }}
    >
      {/* Brand */}
      <Box sx={{ px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        {/* Logo mark */}
        <Box sx={{
          width: 32, height: 32, borderRadius: "9px",
          background: "linear-gradient(135deg,#1565C0,#42A5F5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(21,101,192,.4)",
          flexShrink: 0,
        }}>
          <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".9rem" }}>D</Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.02em" }}>
          Dr.Paw
        </Typography>
        <Chip label="ADMIN" size="small" sx={{
          background: "linear-gradient(135deg,#1565C0,#42A5F5)",
          color: "#FFFFFF", fontWeight: 700, fontSize: "0.6rem", height: 20,
          ml: "auto",
        }} />
      </Box>

      <Divider sx={{ borderColor: ADMIN_COLORS.sidebarBorder }} />

      {/* Navigation */}
      <List sx={{ px: 1.5, py: 2, flex: 1, overflowY: "auto" }}>
        {navItems.map((item, idx) => {
          if (item.section) {
            return (
              <Box key={`section-${idx}`} sx={{ px: 1, pt: idx === 0 ? 0 : 1.5, pb: 0.5 }}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.3)", fontWeight: 800, letterSpacing: ".1em", fontSize: ".62rem" }}>
                  {item.label}
                </Typography>
              </Box>
            );
          }
          const active = isActive(item.path!);
          return (
            <ListItemButton
              key={item.path}
              onClick={() => { navigate(item.path!); if (isMobile) setMobileOpen(false); }}
              sx={{
                borderRadius: "10px", mb: 0.4, px: 1.8, py: 1,
                color: active ? ADMIN_COLORS.textSidebarActive : ADMIN_COLORS.textSidebar,
                bgcolor: active ? ADMIN_COLORS.sidebarActive : "transparent",
                borderLeft: active ? `3px solid ${ADMIN_COLORS.accentLight}` : "3px solid transparent",
                "&:hover": {
                  bgcolor: active ? ADMIN_COLORS.sidebarActive : ADMIN_COLORS.sidebarHover,
                  color: ADMIN_COLORS.textSidebarActive,
                },
                transition: "all 0.15s ease",
              }}
            >
              <ListItemIcon sx={{
                color: active ? ADMIN_COLORS.iconActive : ADMIN_COLORS.textSidebar,
                minWidth: 38,
                "& svg": { fontSize: "1.15rem" },
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: "0.875rem" }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: ADMIN_COLORS.sidebarBorder }} />

      {/* User section */}
      <Box sx={{ px: 2, py: 2 }}>
        {/* User info */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.5, mb: 1.5,
          px: 1.5, py: 1.2, borderRadius: "12px",
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}>
          <Avatar sx={{
            width: 34, height: 34,
            background: "linear-gradient(135deg,#1565C0,#42A5F5)",
            fontSize: "0.85rem", fontWeight: 700, flexShrink: 0,
          }}>
            {user?.name?.charAt(0)?.toUpperCase() || "A"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              color: "#FFFFFF", fontWeight: 600,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {user?.name || "Admin"}
            </Typography>
            <Typography variant="caption" sx={{ color: ADMIN_COLORS.accentLight, fontWeight: 600, fontSize: ".65rem" }}>
              Administrator
            </Typography>
          </Box>
        </Box>

        <ListItemButton onClick={() => navigate("/membership/classes")} sx={{
          borderRadius: "8px", py: 0.9, px: 1.5, mb: 0.5,
          color: ADMIN_COLORS.textSidebar,
          "&:hover": { bgcolor: ADMIN_COLORS.sidebarHover, color: ADMIN_COLORS.textSidebarActive },
        }}>
          <ListItemIcon sx={{ color: "inherit", minWidth: 32, "& svg": { fontSize: "1rem" } }}>
            <ArrowBack fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Back to Site" primaryTypographyProps={{ fontSize: "0.82rem" }} />
        </ListItemButton>

        <ListItemButton onClick={async () => { await logout(); navigate("/login"); }} sx={{
          borderRadius: "8px", py: 0.9, px: 1.5,
          color: ADMIN_COLORS.textSidebar,
          "&:hover": { bgcolor: "rgba(239,68,68,0.15)", color: "#EF4444" },
        }}>
          <ListItemIcon sx={{ color: "inherit", minWidth: 32, "& svg": { fontSize: "1rem" } }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: "0.82rem" }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  const currentPage = navItems.find((item) => !item.section && isActive(item.path!))?.label || "Admin";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: ADMIN_COLORS.bg }}>

      {/* ── Sidebar ─────────────────────────────────────────── */}
      {isMobile ? (
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onOpen={handleDrawerToggle}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH_MOBILE, boxSizing: "border-box", border: "none", bgcolor: ADMIN_COLORS.sidebar } }}
        >
          {drawerContent}
        </SwipeableDrawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH, flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH, boxSizing: "border-box", border: "none",
              bgcolor: ADMIN_COLORS.sidebar,
              boxShadow: "4px 0 24px rgba(10,22,40,0.18)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* ── Main ────────────────────────────────────────────── */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` }, minWidth: 0 }}>

        {/* Top Bar */}
        <AppBar position="sticky" elevation={0} sx={{
          bgcolor: ADMIN_COLORS.topbar,
          color: ADMIN_COLORS.text,
          borderBottom: `1px solid ${ADMIN_COLORS.topbarBorder}`,
          boxShadow: "0 2px 12px rgba(21,101,192,0.07)",
        }}>
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 2 }}>
            {isMobile && (
              <IconButton onClick={handleDrawerToggle} sx={{ color: ADMIN_COLORS.accent }} aria-label="open navigation menu">
                <MenuIcon />
              </IconButton>
            )}

            {/* Page title */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: ADMIN_COLORS.text, fontSize: { xs: ".95rem", sm: "1rem" } }}>
                {currentPage}
              </Typography>
              <Typography variant="caption" sx={{ color: ADMIN_COLORS.textMuted, fontSize: ".72rem" }}>
                Dr.Paw Admin Panel
              </Typography>
            </Box>

            {/* Right: notifications + avatar */}
            <NotificationBell />
            <Avatar sx={{
              width: 36, height: 36,
              background: "linear-gradient(135deg,#1565C0,#42A5F5)",
              fontSize: ".85rem", fontWeight: 700,
              boxShadow: "0 2px 8px rgba(21,101,192,.3)",
            }}>
              {user?.name?.charAt(0)?.toUpperCase() || "A"}
            </Avatar>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box component="main" sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: "auto", maxWidth: "100%" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
